import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Switch,
  VStack,
} from '@chakra-ui/react'
import { apiClient } from '@/lib/api/client'
import { geographicService, type GeographicOption } from '@/lib/services/geographicService'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import type { GeographicEntityConfig } from '@/config/geographicEntities'

const baseSchema = z.object({
  code: z.string().min(1, 'Code is required').max(100),
  active: z.boolean(),
  area_cluster_id: z.union([z.number(), z.nan(), z.null()]).optional(),
  region_id: z.union([z.number(), z.nan(), z.null()]).optional(),
  province_id: z.union([z.number(), z.nan(), z.null()]).optional(),
  name: z.string().optional(),
  province_name: z.string().optional(),
  town_name: z.string().optional(),
})

type FormData = z.infer<typeof baseSchema>

type GeographicEntityFormProps = {
  config: GeographicEntityConfig
  entityId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

export function GeographicEntityForm({
  config,
  entityId,
  onSuccess,
  onCancel,
}: GeographicEntityFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [areaClusters, setAreaClusters] = useState<GeographicOption[]>([])
  const [regions, setRegions] = useState<GeographicOption[]>([])
  const [provinces, setProvinces] = useState<GeographicOption[]>([])
  const isEditMode = !!entityId

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(baseSchema),
    defaultValues: {
      code: '',
      name: '',
      province_name: '',
      town_name: '',
      area_cluster_id: null,
      region_id: null,
      province_id: null,
      active: true,
    },
  })

  const activeValue = watch('active')
  const areaClusterId = watch('area_cluster_id')

  useEffect(() => {
    const loadOptions = async () => {
      try {
        if (config.key === 'geographicRegions' || config.key === 'provinces') {
          setAreaClusters(await geographicService.getAreaClusterOptions())
        }
        if (config.key === 'provinces') {
          setRegions(await geographicService.getRegionOptions())
        }
        if (config.key === 'towns') {
          setProvinces(await geographicService.getProvinceOptions())
        }
      } catch (e) {
        showError(e)
      }
    }
    loadOptions()
  }, [config.key, showError])

  useEffect(() => {
    if (config.key !== 'provinces' || !areaClusterId) return
    geographicService.getProvinceOptions({ areaClusterId: Number(areaClusterId) }).then(setProvinces).catch(showError)
  }, [areaClusterId, config.key, showError])

  useEffect(() => {
    if (!isEditMode || !entityId) return
    const load = async () => {
      setIsLoading(true)
      try {
        const res = await apiClient.get(`${config.apiPath}/${entityId}`)
        const d = res.data?.data ?? res.data ?? {}
        reset({
          code: d.code ?? '',
          name: d.name ?? '',
          province_name: d.province_name ?? '',
          town_name: d.town_name ?? '',
          area_cluster_id: d.area_cluster_id ?? null,
          region_id: d.region_id ?? null,
          province_id: d.province_id ?? null,
          active: d.active === 'Active' || d.active === true,
        })
      } catch (e) {
        showError(e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [config.apiPath, entityId, isEditMode, reset, showError])

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    try {
      const payload: Record<string, unknown> = {
        code: data.code,
        active: data.active,
      }

      if (config.key === 'areaClusters' || config.key === 'geographicRegions') {
        payload.name = data.name
        if (!data.name?.trim()) {
          showError('Name is required')
          setIsSubmitting(false)
          return
        }
      }
      if (config.key === 'geographicRegions') {
        payload.area_cluster_id = data.area_cluster_id || null
      }
      if (config.key === 'provinces') {
        payload.province_name = data.province_name
        if (!data.province_name?.trim()) {
          showError('Province name is required')
          setIsSubmitting(false)
          return
        }
        payload.region_id = data.region_id || null
        payload.area_cluster_id = data.area_cluster_id || null
      }
      if (config.key === 'towns') {
        payload.town_name = data.town_name
        if (!data.town_name?.trim() || !data.province_id) {
          showError('Town name and province are required')
          setIsSubmitting(false)
          return
        }
        payload.province_id = data.province_id
      }

      if (isEditMode && entityId) {
        await apiClient.put(`${config.apiPath}/${entityId}`, payload)
        showSuccess('Success', `${config.singular} updated`)
      } else {
        await apiClient.post(config.apiPath, payload)
        showSuccess('Success', `${config.singular} created`)
      }
      onSuccess?.()
    } catch (e) {
      showError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return <Box>Loading...</Box>
  }

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)}>
      <VStack spacing={4} align="stretch" maxW="640px">
        <FormControl isInvalid={!!errors.code}>
          <FormLabel>Code</FormLabel>
          <Input {...register('code')} />
          <FormErrorMessage>{errors.code?.message}</FormErrorMessage>
        </FormControl>

        {(config.key === 'areaClusters' || config.key === 'geographicRegions') && (
          <FormControl isInvalid={!!errors.name}>
            <FormLabel>{config.nameLabel}</FormLabel>
            <Input {...register('name')} />
            <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
          </FormControl>
        )}

        {config.key === 'provinces' && (
          <FormControl isInvalid={!!errors.province_name}>
            <FormLabel>{config.nameLabel}</FormLabel>
            <Input {...register('province_name')} />
            <FormErrorMessage>{errors.province_name?.message}</FormErrorMessage>
          </FormControl>
        )}

        {config.key === 'towns' && (
          <FormControl isInvalid={!!errors.town_name}>
            <FormLabel>{config.nameLabel}</FormLabel>
            <Input {...register('town_name')} />
            <FormErrorMessage>{errors.town_name?.message}</FormErrorMessage>
          </FormControl>
        )}

        {(config.key === 'geographicRegions' || config.key === 'provinces') && (
          <FormControl>
            <FormLabel>Area Cluster</FormLabel>
            <Select
              placeholder="Select area cluster"
              value={watch('area_cluster_id') ?? ''}
              onChange={(e) =>
                setValue('area_cluster_id', e.target.value ? Number(e.target.value) : null)
              }
            >
              {areaClusters.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        )}

        {config.key === 'provinces' && (
          <FormControl>
            <FormLabel>PH Region</FormLabel>
            <Select
              placeholder="Select PH region"
              value={watch('region_id') ?? ''}
              onChange={(e) => setValue('region_id', e.target.value ? Number(e.target.value) : null)}
            >
              {regions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        )}

        {config.key === 'towns' && (
          <FormControl isInvalid={!!errors.province_id}>
            <FormLabel>Province</FormLabel>
            <Select
              placeholder="Select province"
              value={watch('province_id') ?? ''}
              onChange={(e) =>
                setValue('province_id', e.target.value ? Number(e.target.value) : null)
              }
            >
              {provinces.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.province_id?.message}</FormErrorMessage>
          </FormControl>
        )}

        <FormControl display="flex" alignItems="center">
          <FormLabel mb="0">Active</FormLabel>
          <Switch isChecked={activeValue} onChange={(e) => setValue('active', e.target.checked)} />
        </FormControl>

        <HStack>
          <Button type="submit" colorScheme="blue" isLoading={isSubmitting}>
            {isEditMode ? 'Update' : 'Create'}
          </Button>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
        </HStack>
      </VStack>
    </Box>
  )
}
